/**
 * The remembered half of the columns picker.
 *
 * Which columns a reader has turned off is a preference of that reader on that
 * browser, so it lives in `localStorage` and not in the address bar, keyed per
 * table. Everything about it is allowed to fail: storage can be blocked, full,
 * or holding something an older build wrote. Each of those reads as "nothing
 * hidden", which is a table that still renders correctly, rather than as an
 * exception in the middle of a list page.
 */

/** Where one table's hidden columns are kept. */
export function hiddenColumnsKey(tableKey: string): string {
  return `gojibasel.table.${tableKey}.hidden`
}

/**
 * A stored value, read back as a list of column keys.
 *
 * Anything that is not an array of strings is dropped rather than trusted. A
 * hand-edited value or one written by another build is the likeliest thing to
 * be found there, and a table that throws on it would need storage cleared by
 * hand before it worked again.
 */
export function parseHiddenColumns(raw: string | null): string[] {
  if (raw === null || raw === '') return []
  try {
    const value: unknown = JSON.parse(raw)
    if (!Array.isArray(value)) return []
    return [...new Set(value.filter((item): item is string => typeof item === 'string' && item !== ''))]
  } catch {
    return []
  }
}

/** The minimal storage shape, so a test can hand in a map and a page hands in `localStorage`. */
export interface KeyValueStore {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
}

/** The browser's storage, or nothing where reaching for it throws. */
export function browserStore(): KeyValueStore | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage
  } catch {
    return undefined
  }
}

export function readHiddenColumns(store: KeyValueStore | undefined, tableKey: string): string[] {
  if (store === undefined) return []
  try {
    return parseHiddenColumns(store.getItem(hiddenColumnsKey(tableKey)))
  } catch {
    return []
  }
}

/**
 * The stored choice, or `fallback` when this browser has never saved one for
 * the table. A page uses it to start with its less-used columns behind the
 * picker: a saved choice, including "show everything", always wins over it.
 */
export function readHiddenColumnsOr(
  store: KeyValueStore | undefined,
  tableKey: string,
  fallback: readonly string[],
): string[] {
  if (store === undefined) return [...fallback]
  try {
    const raw = store.getItem(hiddenColumnsKey(tableKey))
    return raw === null ? [...fallback] : parseHiddenColumns(raw)
  } catch {
    return [...fallback]
  }
}

/** Returns whether the write landed. A blocked store is not an error worth showing anybody. */
export function writeHiddenColumns(store: KeyValueStore | undefined, tableKey: string, hidden: readonly string[]): boolean {
  if (store === undefined) return false
  try {
    store.setItem(hiddenColumnsKey(tableKey), JSON.stringify([...new Set(hidden)]))
    return true
  } catch {
    return false
  }
}

/** Turns one column on or off in the hidden set. */
export function toggleHidden(hidden: readonly string[], key: string): string[] {
  return hidden.includes(key) ? hidden.filter((other) => other !== key) : [...hidden, key]
}
